/**
 * Pricing Page — PatrolPuffin Marketing Portal
 *
 * APP-ENTITLEMENT-001 Phase 11: every amount on this page is the public app catalog's (`GET /api/v1/catalog/apps`,
 * the same read the signup basket prices from), rendered by `@puffin-core/marketing-shell`'s `<AppPricingSection>`
 * on all nine Colony sites — the anchors as tier cards, the apps beside the featured anchor with PatrolPuffin's own row
 * marked, the free apps, the published processing rate and the three-store worked example. Nothing here types a
 * price; every "Start your free trial" is the ONE signup URL (`signupUrlFor`), which hands the wizard the app the
 * visitor came for.
 *
 * @module portals/marketing/pages/PricingPage
 */

import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getBrand } from '@puffin-core/brand-assets';
import { createFetchSeam } from '@puffin-core/api-client/fetch-seam';
import {
  AppPricingSection,
  CTASection,
  MarketingSeo,
  breadcrumbList,
  createAppCatalogClient,
  signupUrlFor,
  useAnalytics,
  useAppCatalog,
  usePricingJsonLd,
  webPage,
} from '@puffin-core/marketing-shell';
import { TRIAL_PERIOD_DAYS } from '@puffin-core/status-taxonomy/subscription-status';
import { PATROL_MARKETING_ROUTES } from '../constants/patrolMarketingRoutePaths';

const APP_KEY = 'patrol';
/** The catalog row this site sells — the section features its anchor and marks its row. */
const CATALOG_APP_KEY = 'patrol';
const SITE_ORIGIN = 'https://patrolpuffin.com';
const PRICING_CANONICAL = `${SITE_ORIGIN}/pricing`;
const BRAND = getBrand(APP_KEY);
/** The ONE signup URL, naming the app the visitor came for; absolute, because the wizard lives on ParcelPuffin's site. */
const SIGNUP_HREF = signupUrlFor({ app: CATALOG_APP_KEY, fromOrigin: SITE_ORIGIN });
/**
 * The ONE price source: the public app catalog's client. `createFetchSeam` resolves `globalThis.fetch` at CALL time,
 * so a module-scope client reads whatever fetch the page runs under (a test's stub included).
 */
const CATALOG_CLIENT = createAppCatalogClient({
  fetch: createFetchSeam({ label: 'patrolMarketingPricingPage' }),
});

/**
 * Pricing Page Component
 *
 * @returns {React.JSX.Element} Pricing page
 */
const PricingPage = () => {
  const { t } = useTranslation();
  const { trackPageView, track } = useAnalytics();
  // The catalog is read ONCE by the page, so its JSON-LD and the section it renders agree.
  const catalog = useAppCatalog(CATALOG_CLIENT);
  const productNodes = usePricingJsonLd({ catalog, pageUrl: PRICING_CANONICAL, t });

  useEffect(() => {
    trackPageView('/pricing', t('pricing.seo.title'));
  }, [trackPageView, t]);

  // A "Start your free trial" leaves this site for the wizard; the click is reported before it does.
  const handleOutboundClick = useCallback(
    (href) => {
      track('signup_cta_click', { href, page: 'pricing' });
    },
    [track],
  );

  const jsonLd = useMemo(
    () => [
      webPage({
        appKey: APP_KEY,
        brand: BRAND,
        url: PRICING_CANONICAL,
        title: t('pricing.seo.title'),
        description: t('pricing.seo.description', { days: TRIAL_PERIOD_DAYS }),
      }),
      breadcrumbList({
        crumbs: [
          { name: BRAND.appName, url: `${SITE_ORIGIN}/` },
          { name: t('pricing.hero.title'), url: PRICING_CANONICAL },
        ],
      }),
      ...productNodes,
    ],
    [t, productNodes],
  );

  return (
    <>
      <MarketingSeo
        title={t('pricing.seo.title')}
        description={t('pricing.seo.description', { days: TRIAL_PERIOD_DAYS })}
        canonical={PRICING_CANONICAL}
        ogType="website"
        ogImage={`${SITE_ORIGIN}${BRAND.assets.ogCard}`}
        jsonLd={jsonLd}
      />
      <div data-testid="pricing-page">
        <section className="hero" aria-labelledby="pricing-heading" data-testid="pricing-hero">
          <div className="hero-content">
            <h1 id="pricing-heading" className="hero-title">
              {t('pricing.hero.title')}
            </h1>
            <p className="hero-subtitle">
              {t('pricing.hero.subtitle', { days: TRIAL_PERIOD_DAYS })}
            </p>
          </div>
        </section>
        <AppPricingSection
          appKey={CATALOG_APP_KEY}
          catalog={catalog}
          fromOrigin={SITE_ORIGIN}
          t={t}
          onOutboundClick={handleOutboundClick}
        />
        <CTASection
          heading={t('pricing.cta.heading')}
          subheading={t('pricing.cta.subheading', { days: TRIAL_PERIOD_DAYS })}
          primaryAction={t('pricing.cta.primaryAction')}
          primaryLink={SIGNUP_HREF}
          secondaryAction={t('pricing.cta.secondaryAction')}
          secondaryLink={PATROL_MARKETING_ROUTES.CONTACT}
          onOutboundClick={handleOutboundClick}
        />
      </div>
    </>
  );
};

export default PricingPage;
